/* eslint-disable */
/**
 * SagerPanel.jsx — Panel 1.4 i nyt OS-dashboard
 *
 * Datapunkter og status-mærkning:
 *   /api/os/intern/statistik        → LIVE   (direkte fra DB via os-auth)
 *   /api/os/intern/sager-oversigt   → LIVE   (status/fase-fordeling)
 *   /api/os/intern/aktivitet        → LIVE   (30-dages aktivitets-tidsserie → sparkline)
 *
 * Regler:
 *   - Ingen fake/hardcoded tal
 *   - Alle KPI-kort viser LIVE badge (data kommer direkte fra prod-DB)
 *   - Inaktive sager (>14 dage) markeret med OPMÆRKSOMHED
 *   - Overskredne deadlines markeret med FEJL
 *   - TomTilstand hvis endpoint fejler eller returnerer tomt
 */

import React, { useState, useCallback } from 'react';
import { FileText, Users, Activity, Clock, CheckCircle2, BarChart3 } from 'lucide-react';
import {
  KpiKort,
  StatusBadge,
  TomTilstand,
  DataTabel,
  TidslinjeListe,
  GrafLinje,
  SektionsHeader,
  AfventerBadge,
  SammenklappeligSektion,
  useApiPolling,
  useWindowWidth,
  STATUS,
  TEKST,
  BG,
  KANT,
  RADIUS,
  SPACING,
} from '../komponenter/index';
import { beregnSparklineData } from '../ui/panelUtils';
import { Boks } from '../ui/Boks';

// ─── Hjælpefunktioner ─────────────────────────────────────────────────────────

function formatDato(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function faseLabel(fase) {
  const map = {
    '0': 'Indgang',
    '1': 'Afklaring',
    '2': 'Aktiv',
    '3': 'Stabilisering',
    '4': 'Afslutning',
    'ukendt': 'Ukendt',
  };
  return map[String(fase)] || `Fase ${fase}`;
}

// Transformér sager-oversigt til kort-liste (status → farve)
const statusFarve = {
  aktiv:     STATUS.live,
  afsluttet: STATUS.konfigureret,
  afventer:  STATUS.opmærksomhed,
  pause:     STATUS.ikkeForbundet,
};

// ─── Mobil: status-oversigt som kort-liste (ingen vandret scroll) ─────────────
function SagsStatusKortListe({ rækker }) {
  if (!rækker || rækker.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rækker.map((r, i) => {
        const deadlineN = parseInt(r.overskredet_deadline) || 0;
        const badgeStatus = r.status === 'aktiv' ? 'LIVE'
          : r.status === 'afsluttet' ? 'KONFIGURERET'
          : 'OPMÆRKSOMHED';
        return (
          <div
            key={r.status ?? i}
            style={{
              background:   BG.kort,
              border:       `1px solid ${KANT.standard}`,
              borderLeft:   `3px solid ${statusFarve[r.status] ?? KANT.standard}`,
              borderRadius: RADIUS.kort,
              padding:      '12px 14px',
            }}
          >
            {/* Række 1: Status + antal */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 8 }}>
              <StatusBadge status={badgeStatus} tekst={r.status} lille />
              <span style={{ fontSize: 20, fontWeight: 800, color: TEKST.primær }}>{r.antal}</span>
            </div>
            {/* Række 2: Gns. alder */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: TEKST.sekundær, marginBottom: 4 }}>
              <span>Gns. alder</span>
              <span>{r.gns_alder_dage != null ? `${Math.round(parseFloat(r.gns_alder_dage))} dage` : '—'}</span>
            </div>
            {/* Række 3: Deadline overskredet */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: TEKST.sekundær }}>
              <span>Deadline oversk.</span>
              {deadlineN > 0
                ? <StatusBadge status="FEJL" tekst={`${deadlineN} sager`} lille puls />
                : <span style={{ color: STATUS.live }}>Ingen</span>
              }
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ─── PANEL ────────────────────────────────────────────────────────────────────
export default function SagerPanel() {
  // ── Responsive ─────────────────────────────────────────────────────────────
  const isMobil = useWindowWidth() < 600;

  // ── Data-fetching ──────────────────────────────────────────────────────────
  const {
    data:      stat,
    loading:   statLoading,
    fejl:      statFejl,
    opdateret: statOpdateret,
    refresh:   statRefresh,
  } = useApiPolling('/os/intern/statistik', 90000);

  const {
    data:      oversigt,
    loading:   oversigtLoading,
    refresh:   oversigtRefresh,
  } = useApiPolling('/os/intern/sager-oversigt', 60000);

  const {
    data:      aktivitet,
    loading:   aktivitetLoading,
  } = useApiPolling('/os/intern/aktivitet', 120000);

  // Første-load flags: skeleton vises KUN når data endnu ikke er hentet.
  // Ved baggrunds-refresh forbliver eksisterende data synlige → scroll bevares.
  const statFørsteLoad      = statLoading && !stat;
  const oversigtFørsteLoad  = oversigtLoading && !oversigt;
  const aktivitetFørsteLoad = aktivitetLoading && !aktivitet;

  // ── Refresh alle ───────────────────────────────────────────────────────────
  const [refreshing, setRefreshing] = useState(false);
  const refreshAlt = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([statRefresh(), oversigtRefresh()]);
    setRefreshing(false);
  }, [statRefresh, oversigtRefresh]);

  // ── Udled data ─────────────────────────────────────────────────────────────

  // Aktivitets-sparkline: 30 dage, nyeste til højre
  const sparklineData = beregnSparklineData(aktivitet);

  // Sagsfordeling (status)
  const sagsOversigt = oversigt?.oversigt || [];

  // Fase-fordeling (fra statistik)
  const faseFordeling = stat?.sager_pr_fase
    ? Object.entries(stat.sager_pr_fase).map(([fase, antal]) => ({
        fase: faseLabel(fase),
        antal,
        _fase_raw: fase,
      }))
    : [];

  // Inaktive kolonner til DataTabel (vi viser kun sagsfordeling per status)
  const oversigtKolonner = [
    {
      key:    'status',
      label:  'Status',
      render: (v) => (
        <StatusBadge
          status={v === 'aktiv' ? 'LIVE' : v === 'afsluttet' ? 'KONFIGURERET' : 'OPMÆRKSOMHED'}
          tekst={v}
          lille
        />
      ),
    },
    {
      key:    'antal',
      label:  'Antal',
      align:  'right',
      render: v => <span style={{ fontWeight: 700, color: TEKST.primær }}>{v}</span>,
    },
    {
      key:    'gns_alder_dage',
      label:  'Gns. alder',
      align:  'right',
      render: v => v != null ? <span style={{ color: TEKST.sekundær }}>{Math.round(parseFloat(v))} dage</span> : '—',
    },
    {
      key:    'overskredet_deadline',
      label:  'Deadline oversk.',
      align:  'right',
      render: v => {
        const n = parseInt(v) || 0;
        return n > 0
          ? <StatusBadge status="FEJL" tekst={`${n} sager`} lille puls />
          : <span style={{ color: STATUS.live, fontSize: 11 }}>Ingen</span>;
      },
    },
  ];

  // Tidslinje: hent de seneste haendelser-punkter som simpel liste
  //
  // node-postgres serialiserer PostgreSQL DATE-kolonner til fulde ISO-strenge
  // med Z-suffix (fx "2026-06-14T00:00:00.000Z") — ikke bare "YYYY-MM-DD".
  // At appende 'T00:00:00' på en allerede-fuld ISO-streng → ugyldig dato.
  // Fix: brug r.dato direkte (virker for begge formater) og lav en robust
  // toString-konvertering der altid giver "YYYY-MM-DD" til visningsbrug.
  const aktivitetTidslinje = (() => {
    const rækker = aktivitet?.aktivitet;
    if (!Array.isArray(rækker)) return [];
    return [...rækker]
      .sort((a, b) => new Date(b.dato) - new Date(a.dato))
      .slice(0, 12)
      .map((r, i) => ({
        id:    i,
        dato:  r.dato,          // ISO-streng eller Date — new Date() håndterer begge
        titel: `${r.haendelser} handlinger`,
        tekst: `${r.aktive_sager} aktive sager den dag`,
        farve: STATUS.konfigureret,
        ikon:  <Activity size={14} />,
      }));
  })();

  // ── Loading-state ──────────────────────────────────────────────────────────
  // alLoading bruges kun ved første load (alle tre datasæt mangler endnu)
  const alLoading = statFørsteLoad && oversigtFørsteLoad;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* Panel-header */}
      <SektionsHeader
        label="Sager"
        opdateret={statOpdateret}
        onRefresh={refreshAlt}
        refreshing={refreshing}
        højreIndhold={
          stat?.inaktive_14d > 0
            ? <AfventerBadge antal={stat.inaktive_14d} label="inaktive >14 dage" />
            : null
        }
        style={{ marginBottom: 20 }}
      />

      {/* ── KPI-række ───────────────────────────────────────────────────────── */}
      {/* Mobil: 2-kolonne grid (5 kort → 3+2). Desktop: flex-wrap (pixel-identisk med v1.6.60). */}
      <div style={isMobil ? {
        display:             'grid',
        gridTemplateColumns: '1fr 1fr',
        gap:                 12,
        marginBottom:        18,
      } : {
        display:     'flex',
        gap:         SPACING.gap,
        flexWrap:    'wrap',
        marginBottom: 22,
      }}>
        <KpiKort
          label="Aktive sager"
          kortLabel="AKTIVE"
          value={stat?.aktive_sager}
          farve={STATUS.live}
          ikon={<FileText size={16} />}
          badge="LIVE"
          loading={statFørsteLoad}
          mobil={isMobil}
        />
        <KpiKort
          label="Nye denne uge"
          kortLabel="NYE/UGE"
          value={stat?.nye_denne_uge}
          farve={STATUS.konfigureret}
          ikon={<Users size={16} />}
          badge="LIVE"
          loading={statFørsteLoad}
          mobil={isMobil}
        />
        <KpiKort
          label="Handlinger (7d)"
          kortLabel="HANDL. 7D"
          value={stat?.handlinger_7d}
          farve={STATUS.ai}
          ikon={<Activity size={16} />}
          badge="LIVE"
          delta={stat != null && stat.handlinger_30d > 0
            ? Math.round((stat.handlinger_7d / (stat.handlinger_30d / 4)) * 100) - 100
            : null}
          deltaLabel="vs. gns./uge"
          loading={statFørsteLoad}
          mobil={isMobil}
        />
        <KpiKort
          label="Inaktive >14 dage"
          kortLabel="INAKTIVE"
          value={stat?.inaktive_14d}
          farve={stat?.inaktive_14d > 0 ? STATUS.opmærksomhed : STATUS.live}
          ikon={<Clock size={16} />}
          badge="LIVE"
          loading={statFørsteLoad}
          mobil={isMobil}
        />
        <KpiKort
          label="Lukkede sager"
          kortLabel="LUKKEDE"
          value={stat?.lukkede_sager}
          farve={STATUS.ikkeForbundet}
          ikon={<CheckCircle2 size={16} />}
          badge="LIVE"
          loading={statFørsteLoad}
          mobil={isMobil}
        />
      </div>

      {/* ── To-kolonne: Sagsfordeling + Sparkline — sekundært, sammenklap mobil ─ */}
      <SammenklappeligSektion
        titel="Sagsfordeling & aktivitet"
        defaultÅben={false}
        isMobil={isMobil}
        style={{ marginBottom: 22 }}
      >
      <div style={{ display: 'grid', gridTemplateColumns: isMobil ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>

        {/* Sagsfordeling per status */}
        <Boks blur={12} padding={SPACING.kortPadding}>
          <SektionsHeader label="Fordeling per status" style={{ marginBottom: 12 }} />
          {oversigtFørsteLoad ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
          ) : sagsOversigt.length === 0 ? (
            <TomTilstand
              titel="Ingen sagsdata"
              besked="Returneres fra /api/os/intern/sager-oversigt"
              ikon="▦"
              badge="LIVE"
            />
          ) : isMobil ? (
            <SagsStatusKortListe rækker={sagsOversigt} />
          ) : (
            <DataTabel
              kolonner={oversigtKolonner}
              rækker={sagsOversigt}
              pageSize={0}
              tomTekst="Ingen sager"
            />
          )}
        </Boks>

        {/* Aktivitets-sparkline */}
        <Boks blur={12} padding={SPACING.kortPadding}>
          <SektionsHeader label="Aktivitet — 30 dage" style={{ marginBottom: 12 }} />
          {aktivitetFørsteLoad ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
          ) : sparklineData ? (
            <>
              <GrafLinje
                data={sparklineData}
                farve={STATUS.konfigureret}
                højde={100}
                fyldt={true}
              />
              <div style={{ marginTop: 8, fontSize: 11, color: TEKST.sekundær }}>
                Handlinger per dag — seneste 30 dage
                <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
              </div>
            </>
          ) : (
            <TomTilstand
              titel="Ikke nok aktivitetsdata"
              besked="Kræver min. 2 dages data fra /api/os/intern/aktivitet"
              ikon="↗"
              badge="LIVE"
            />
          )}
        </Boks>
      </div>
      </SammenklappeligSektion>

      {/* ── To-kolonne: Fase-fordeling + Inaktive sager — sekundært ─────────── */}
      <SammenklappeligSektion
        titel="Fase & inaktive sager"
        defaultÅben={false}
        isMobil={isMobil}
        style={{ marginBottom: 22 }}
      >
      <div style={{ display: 'grid', gridTemplateColumns: isMobil ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>

        {/* Fase-fordeling */}
        <Boks blur={12} padding={SPACING.kortPadding}>
          <SektionsHeader label="Aktive sager per fase" style={{ marginBottom: 12 }} />
          {statFørsteLoad ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
          ) : faseFordeling.length === 0 ? (
            <TomTilstand
              titel="Ingen faseopdeling"
              besked="Data hentes fra sager.fase-kolonnen"
              ikon="◎"
              badge="LIVE"
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {faseFordeling
                .sort((a, b) => a._fase_raw - b._fase_raw)
                .map(f => {
                  const max = Math.max(...faseFordeling.map(x => x.antal), 1);
                  const pct = Math.round((f.antal / max) * 100);
                  return (
                    <div key={f._fase_raw}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: TEKST.primær }}>{f.fase}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: STATUS.live }}>{f.antal}</span>
                      </div>
                      <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                        <div style={{
                          width: `${pct}%`,
                          height: '100%',
                          background: STATUS.live,
                          borderRadius: 4,
                          transition: 'width .5s ease',
                        }} />
                      </div>
                    </div>
                  );
                })
              }
              <div style={{ marginTop: 6, fontSize: 11, color: TEKST.sekundær }}>
                <StatusBadge status="LIVE" lille style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Live data fra sagsportal
              </div>
            </div>
          )}
        </Boks>

        {/* Inaktive sager */}
        <Boks blur={12} padding={SPACING.kortPadding}>
          <SektionsHeader
            label="Kræver opfølgning"
            højreIndhold={
              stat?.inaktive_14d > 0
                ? <StatusBadge status="OPMÆRKSOMHED" tekst={`${stat.inaktive_14d} sager`} puls />
                : <StatusBadge status="LIVE" tekst="Alle aktive" />
            }
            style={{ marginBottom: 12 }}
          />
          {statFørsteLoad ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
          ) : !stat ? (
            <TomTilstand
              titel="Statistik ikke tilgængeligt"
              besked="Kontrollér /api/os/intern/statistik"
              ikon="⚠"
              badge="FEJL"
            />
          ) : (
            <div>
              {/* Inaktive sager boks */}
              <div style={{
                background:  stat.inaktive_14d > 0 ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.06)',
                border:      `1px solid ${stat.inaktive_14d > 0 ? 'rgba(245,158,11,0.25)' : 'rgba(34,197,94,0.20)'}`,
                borderRadius: RADIUS.kort - 2,
                padding:     '14px 16px',
                marginBottom: 12,
              }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: stat.inaktive_14d > 0 ? STATUS.opmærksomhed : STATUS.live, marginBottom: 4 }}>
                  {stat.inaktive_14d ?? '—'}
                </div>
                <div style={{ fontSize: 12, color: TEKST.sekundær }}>
                  Aktive sager uden ny aktivitet i mere end 14 dage
                </div>
                {stat.inaktive_14d > 0 && (
                  <div style={{ marginTop: 8, fontSize: 11, color: STATUS.opmærksomhed, fontWeight: 600 }}>
                    Daisy: gå til sagsportalen og tjek disse sager
                  </div>
                )}
              </div>

              {/* Overskredet deadlines fra sager-oversigt */}
              {sagsOversigt.some(r => parseInt(r.overskredet_deadline) > 0) && (
                <div style={{
                  background:  'rgba(239,68,68,0.08)',
                  border:      '1px solid rgba(239,68,68,0.25)',
                  borderRadius: RADIUS.kort - 2,
                  padding:     '10px 14px',
                }}>
                  <div style={{ fontSize: 12, color: STATUS.fejl, fontWeight: 700, marginBottom: 4 }}>
                    Overskredne deadlines
                  </div>
                  {sagsOversigt
                    .filter(r => parseInt(r.overskredet_deadline) > 0)
                    .map(r => (
                      <div key={r.status} style={{ fontSize: 12, color: TEKST.sekundær }}>
                        {r.status}: {r.overskredet_deadline} sager
                      </div>
                    ))
                  }
                </div>
              )}

              {/* Alt OK */}
              {stat.inaktive_14d === 0 && !sagsOversigt.some(r => parseInt(r.overskredet_deadline) > 0) && (
                <div style={{ fontSize: 12, color: STATUS.live }}>
                  Alle aktive sager er ajour — ingen deadlines overskredet.
                </div>
              )}
            </div>
          )}
        </Boks>
      </div>
      </SammenklappeligSektion>

      {/* ── Aktivitets-tidslinje (30 dage) — sekundært ───────────────────────── */}
      <SammenklappeligSektion
        titel="Aktivitetstidslinje"
        defaultÅben={false}
        isMobil={isMobil}
      >
      <Boks blur={12} padding={SPACING.kortPadding}>
        <SektionsHeader label="Aktivitetsoversigt — seneste 30 dage" style={{ marginBottom: 12 }} />
        <TidslinjeListe
          items={aktivitetTidslinje}
          tomTekst="Ingen aktivitet registreret"
          loading={aktivitetFørsteLoad}
          maxHøjde={280}
        />
        <div style={{ marginTop: 10, fontSize: 11, color: TEKST.sekundær }}>
          Data: haendelser-tabel · aggregeret per dag
          <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
        </div>
      </Boks>
      </SammenklappeligSektion>

    </div>
  );
}
